import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../services/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { UserProfile } from '../types';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  setProfileLocal: (profile: UserProfile) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  setProfileLocal: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Optimistic update helper
  const setProfileLocal = (newProfile: UserProfile) => {
    setProfile(newProfile);
  };

  useEffect(() => {
    // Critical safety check: If auth is undefined (init failed), do not attempt to subscribe
    if (!auth) {
      console.error("Auth Service not initialized. Check Firebase config.");
      setLoading(false);
      return;
    }

    let unsubscribeProfile: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      // Always clean up the previous profile listener when auth state changes
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (currentUser) {
        setUser(currentUser);
        
        // Subscribe to the user profile in real-time.
        // This reads from the local cache IMMEDIATELY (offline support), 
        // then syncs with the server in the background.
        if (!db) {
           console.error("Firestore DB not initialized. Profile sync aborted.");
           setLoading(false);
           return;
        }

        unsubscribeProfile = onSnapshot(
          doc(db, 'users', currentUser.uid), 
          (docSnap) => {
            if (docSnap.exists()) {
              setProfile(docSnap.data() as UserProfile);
            } else {
              // User authenticated, but no profile doc exists yet
              console.log("Auth verified, but no User Profile found in Firestore for UID:", currentUser.uid);
              setProfile(null);
            }
            // Data is loaded (either found or confirmed missing), stop loading
            setLoading(false);
          }, 
          (error) => {
            console.error("Profile sync error (onSnapshot):", error.message);
            if (error.code === 'permission-denied') {
                console.error("Check Firestore Security Rules. The app cannot read the user profile.");
            }
            // Even on error, stop loading so the user isn't stuck on a spinner
            setLoading(false);
          }
        );
      } else {
        // User logged out
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, profile, loading, setProfileLocal }}>
      {children}
    </AuthContext.Provider>
  );
};
